/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import Modal from '../../../components/Modal';
import { request } from '../../../config/helpers/axios-instance';
import { toast } from 'react-toastify';
import axios from 'axios';

const MANUAL_DETAILS_FIELDS = [
    'mrp',
    'url',
    'uses',
    'images',
    'available',
    'pack_size',
    'how_to_use',
    'description',
    'dosage_form',
    'generic_name',
    'side_effects',
    'medicine_name',
    'how_drug_works',
    'is_habit_forming',
    'manufacturer_name',
    'storage_conditions',
    'prescription_required',
    'strength_concentration',
    'schedule_classification',
    'prohibited_for_online_sale',
    'warnings_statutory_labelling',
    'composition_active_ingredients'
];

const INTENDED_USE_SUBFIELDS = ['action_class', 'chemical_class', 'therapeutic_class'];

const CHECKBOX_FIELDS = ['available', 'is_habit_forming', 'prohibited_for_online_sale'];

const YES_NO_FIELDS = ['is_habit_forming', 'prohibited_for_online_sale'];

const getLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const toBool = (val) => val === true || val === 'true' || val === '1' || val === 1;

const toYesNoChecked = (val) => String(val || '').trim().toLowerCase() === 'yes';

export default function AddManualDetailsModal({ onClose, item, onSuccess, isEdit = false }) {
    const existingDetails = item?.itemDetail?.details || {};
    const existingIntendedUse = existingDetails?.intended_use_therapeutic_category || {};

    const [availableOnTata, setAvailableOnTata] = useState(false);
    const [tataUrl, setTataUrl] = useState('');
    const [scraping, setScraping] = useState(false);

    const handleScrape = async () => {
        if (!tataUrl.trim()) {
            toast.warn('Please enter a Tata 1mg URL');
            return;
        }
        setScraping(true);
        try {
            await axios.post('https://pricemaster.pillup.com/api/scrapeUrl', {
                url: tataUrl.trim(),
                itemId: item?.id,
            }, {
                headers: {
                    'Authorization': 'pm-pillup',
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Scrape triggered successfully');
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to scrape URL');
        } finally {
            setScraping(false);
        }
    };

    const [formData, setFormData] = useState(() => {
        const initial = {};
        MANUAL_DETAILS_FIELDS.forEach((field) => {
            const val = existingDetails[field];
            if (CHECKBOX_FIELDS.includes(field)) {
                initial[field] = YES_NO_FIELDS.includes(field) ? toYesNoChecked(val) : toBool(val);
            } else {
                initial[field] = val != null ? String(val) : '';
            }
        });
        INTENDED_USE_SUBFIELDS.forEach((field) => {
            const val = existingIntendedUse[field];
            initial[`intended_use_${field}`] = val != null ? String(val) : '';
        });
        return initial;
    });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const details = {};
            MANUAL_DETAILS_FIELDS.forEach((field) => {
                if (CHECKBOX_FIELDS.includes(field)) {
                    details[field] = YES_NO_FIELDS.includes(field)
                        ? (formData[field] ? 'Yes' : 'No')
                        : !!formData[field];
                } else {
                    const val = formData[field]?.trim();
                    if (val !== '') details[field] = val;
                }
            });
            details.intended_use_therapeutic_category = {};
            INTENDED_USE_SUBFIELDS.forEach((field) => {
                const val = formData[`intended_use_${field}`]?.trim();
                if (val !== '') details.intended_use_therapeutic_category[field] = val;
            });
            if (Object.keys(details.intended_use_therapeutic_category).length === 0) {
                delete details.intended_use_therapeutic_category;
            }

            await request({
                url: `item/admin/itemDetails/${item?.id}`,
                method: 'PUT',
                data: { details },
            });
            toast.success(isEdit ? 'Details updated successfully' : 'Details added successfully');
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to add details');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal title={isEdit ? 'Edit Details' : 'Add Details Manually'} onClose={onClose} size="lg">
            <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(e);
            }} className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-sm font-semibold text-gray-800">Available on Tata 1mg?</span>
                        <input
                            type="checkbox"
                            checked={availableOnTata}
                            onChange={(e) => setAvailableOnTata(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                    </label>
                    {availableOnTata && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700">Tata 1mg URL</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="https://www.1mg.com/drugs/..."
                                    className="flex-1 rounded-md h-9 text-sm px-2 border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                    value={tataUrl}
                                    onChange={(e) => setTataUrl(e.target.value)}
                                />
                                <button
                                    type="button"
                                    disabled={scraping}
                                    onClick={handleScrape}
                                    className="px-4 py-1.5 rounded-lg bg-green-500 text-white-50 text-sm font-medium hover:bg-green-600 disabled:opacity-50 transition-colors"
                                >
                                    {scraping ? 'Scraping...' : 'Scrape'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {!availableOnTata && MANUAL_DETAILS_FIELDS.map((field) => (
                    <div key={field} className="flex flex-col gap-1">
                        {CHECKBOX_FIELDS.includes(field) ? (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <span className="text-sm font-medium text-gray-700">{getLabel(field)}</span>
                                <input
                                    type="checkbox"
                                    checked={!!formData[field]}
                                    onChange={(e) => handleChange(field, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                            </label>
                        ) : (
                            <>
                                <label className="text-sm font-medium text-gray-700">{getLabel(field)}</label>
                                <input
                                    type="text"
                                    className="rounded-md h-9 text-sm px-2 border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                    value={formData[field] || ''}
                                    onChange={(e) => handleChange(field, e.target.value)}
                                />
                            </>
                        )}
                    </div>
                ))}
                {!availableOnTata && (
                    <>
                        <div className="border-t pt-4 mt-2">
                            <label className="text-sm font-semibold text-gray-800 block mb-2">Intended Use / Therapeutic Category</label>
                            <div className="flex flex-col gap-3 pl-2">
                                {INTENDED_USE_SUBFIELDS.map((field) => (
                                    <div key={field} className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">{getLabel(field)}</label>
                                        <input
                                            type="text"
                                            className="rounded-md h-9 text-sm px-2 border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                            value={formData[`intended_use_${field}`] || ''}
                                            onChange={(e) => handleChange(`intended_use_${field}`, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 py-2 rounded-lg bg-blue-500 text-white-50 font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
                            >
                                {submitting ? 'Saving...' : (isEdit ? 'Update Details' : 'Save Details')}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </form>
        </Modal>
    );
}
